import React, { useMemo } from 'react';
import './FrontierChart.css';

// Ink-style efficient frontier. Every input is in percent: { risk, return }.
// Scales derive from the data so the same component serves the landing page
// (a few illustrative points) and the note (a 30-point frontier).

function niceTicks(min, max, count) {
  const span = max - min || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const start = Math.ceil(min / step) * step;
  const ticks = [];
  for (let v = start; v <= max + 1e-9; v += step) ticks.push(Number(v.toFixed(6)));
  return ticks;
}

const describe = (d) => `${d.risk.toFixed(1)}% vol · ${d.return.toFixed(1)}% return`;

function Dot({ cx, cy, r, kind, label, sub, dx, dy, strong }) {
  return (
    <g className={`dd-frontier-dot dd-frontier-dot--${kind}`}>
      <circle cx={cx} cy={cy} r={r}>
        <title>{`${label}: ${sub}`}</title>
      </circle>
      <text x={cx + dx} y={cy + dy} className={strong ? 'dd-frontier-label dd-frontier-label--strong' : 'dd-frontier-label'}>{label}</text>
      <text x={cx + dx} y={cy + dy + 14} className="dd-frontier-sub dd-mono">{sub}</text>
    </g>
  );
}

export default function FrontierChart({
  frontier = [],
  current,
  maxSharpe,
  minVol,
  riskParity,
  height = 300,
  caption,
  ariaLabel = 'Efficient frontier',
  animate = false,
}) {
  const W = 560;
  const H = height;
  const L = 52;
  const R = 16;
  const T = 18;
  const B = 40;

  const model = useMemo(() => {
    const ok = (p) => p && Number.isFinite(p.risk) && Number.isFinite(p.return);
    const pts = frontier.filter(ok).slice().sort((a, b) => a.risk - b.risk);
    const dots = [minVol, maxSharpe, riskParity, current].filter(ok);
    const all = [...pts, ...dots];
    if (!all.length) return null;
    const xs = all.map((p) => p.risk);
    const ys = all.map((p) => p.return);
    const xMin = Math.max(0, Math.min(...xs) - 1.5);
    const xMax = Math.max(...xs) + 1.5;
    const yMin = Math.min(0, Math.min(...ys) - 2);
    const yMax = Math.max(...ys) + 2;
    const x = (v) => L + ((v - xMin) / (xMax - xMin || 1)) * (W - L - R);
    const y = (v) => T + (1 - (v - yMin) / (yMax - yMin || 1)) * (H - T - B);
    return { pts, x, y, yTicks: niceTicks(yMin, yMax, 4), xTicks: niceTicks(xMin, xMax, 4) };
  }, [frontier, current, maxSharpe, minVol, riskParity, H]);

  if (!model) {
    return (
      <div className="dd-frontier dd-frontier--empty" style={{ height }}>
        <span className="dd-mono dd-muted">{caption || 'No frontier yet'}</span>
      </div>
    );
  }

  const { pts, x, y, yTicks, xTicks } = model;
  const line = pts.map((p) => `${x(p.risk).toFixed(1)},${y(p.return).toFixed(1)}`).join(' ');

  return (
    <figure className={`dd-frontier ${animate ? 'dd-frontier--animate' : ''}`}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={ariaLabel}>
        <g className="dd-frontier-grid">
          {yTicks.map((t) => <line key={`y${t}`} x1={L} x2={W - R} y1={y(t)} y2={y(t)} />)}
        </g>
        <g className="dd-frontier-axis">
          <line x1={L} x2={L} y1={T} y2={H - B} />
          <line x1={L} x2={W - R} y1={H - B} y2={H - B} />
        </g>
        <g className="dd-frontier-ticks dd-mono">
          {yTicks.map((t) => <text key={`yl${t}`} x={L - 8} y={y(t) + 4} textAnchor="end">{t}%</text>)}
          {xTicks.map((t) => <text key={`xl${t}`} x={x(t)} y={H - B + 16} textAnchor="middle">{t}%</text>)}
          <text x={W - R} y={H - 4} textAnchor="end">annualised volatility →</text>
        </g>
        {line && <polyline className="dd-frontier-line" points={line} pathLength={1} />}
        {minVol && <Dot cx={x(minVol.risk)} cy={y(minVol.return)} r={6} kind="minvol" label="Min volatility" sub={describe(minVol)} dx={10} dy={16} />}
        {riskParity && <Dot cx={x(riskParity.risk)} cy={y(riskParity.return)} r={6} kind="rp" label="Risk parity" sub={describe(riskParity)} dx={10} dy={-10} />}
        {maxSharpe && <Dot cx={x(maxSharpe.risk)} cy={y(maxSharpe.return)} r={7} kind="maxsharpe" label="Max Sharpe" sub={describe(maxSharpe)} dx={12} dy={-8} />}
        {current && maxSharpe && (
          <line className="dd-frontier-leader" x1={x(current.risk)} y1={y(current.return)} x2={x(maxSharpe.risk)} y2={y(maxSharpe.return)} />
        )}
        {current && <Dot cx={x(current.risk)} cy={y(current.return)} r={8} kind="you" label="You" sub={describe(current)} dx={14} dy={18} strong />}
        {animate && current && maxSharpe && (
          <circle
            className="dd-frontier-ghost"
            cx={x(current.risk)}
            cy={y(current.return)}
            r={5}
            style={{ '--dx': `${(x(maxSharpe.risk) - x(current.risk)).toFixed(1)}px`, '--dy': `${(y(maxSharpe.return) - y(current.return)).toFixed(1)}px` }}
          />
        )}
      </svg>
      {caption && <figcaption className="dd-mono dd-muted">{caption}</figcaption>}
    </figure>
  );
}
